import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Paper from '@material-ui/core/Paper';
import ErrorComponent from '../ErrorComponent';
import CircularLoading from '../CircularLoading';
import './GraphTile.scss';

const FULL_DISPLAY = 'full';
const FULL_ROW_DISPLAY = 'full-row';
const HIDDEN_DISPLAY = 'hidden';
const NORMAL_DISPLAY = 'normal';

const FLEX_STYLE = { flex: '1 1 auto', minHeight: '0', minWidth: '0' };

const makeButton = ({ action, id }) => {
  const {
    getTooltip,
    tooltip,
    getIcon,
    icon,
    getAriaLabel,
    ariaLabel,
    onClick,
    className,
    getId,
  } = action;

  const ActionButton = () => {
    const handleClick = useCallback(() => {
      onClick(id);
    });
    const Icon = getIcon ? getIcon(id) : icon;

    const content = (
      <IconButton
        color="primary"
        aria-label={getAriaLabel ? getAriaLabel(id) : ariaLabel}
        onClick={handleClick}
        className={classnames('grb-graph-tile-icon-button', className)}
        id={getId(id) || undefined}
      >
        <Icon />
      </IconButton>
    );
    const tp = getTooltip ? getTooltip(id) : tooltip;
    if (tp) {
      return (
        <Tooltip title={tp}>
          {content}
        </Tooltip>
      );
    }
    return (content);
  };

  return ActionButton;
};

const makeButtonKey = i => i.toString();

function GraphTile(props) {
  const {
    id,
    name,
    icon,
    title,
    Component,
    style,
    display,
    toolbar,
    children,
    onChangeDisplay,
    Icon,
    className,
    disableFullDisplay,
    noHeader,
    elevation,
    actions,
    barActions,
    apiClient,
    onLoad,
    requestURL,
    updateTime,
    computeData,
    centeredLoading,
    loading: externalLoading,
    error: externalError,
    ...rest
  } = props;
  const [HeaderIcon] = useState(() => {
    return Icon || (Boolean(icon) && props.viewService.getIcon(icon));
  });
  const [{ loading, error, data }, setData] = useState({ loading: !!(onLoad || requestURL) });
  const { heightStyle } = useMemo(() => {
    return { heightStyle: rest.configuration.height ? { height: rest.configuration.height } : undefined };
  }, [rest.configuration.height]);

  // Buttons
  const buttons = useMemo(() => {
    return barActions.map((a) => {
      if (!actions[a]) {
        console.error('Unkown action ', a);
        return undefined;
      }
      const action = actions[a];
      return {
        Button: makeButton({ action, id }),
        isValid: action.isValid ? () => action.isValid(id) : () => true,
      };
    }).filter(x => x);
  }, [id, actions, barActions]);

  // Request data, if data props specified
  useEffect(() => {
    if (onLoad || (requestURL && apiClient)) {
      setData({ loading: true });
      (onLoad ? onLoad() : apiClient.get(requestURL))
      .then((res) => {
        let d = onLoad ? res : res.data;
        if (computeData) {
          d = computeData(d, rest); // Only static props such as services will be used from createChartModel
        }
        setData({ loading: false, error: null, data: d });
      })
      .catch((e) => {
        setData({ loading: false, error: e });
      });
    }
  }, [updateTime, requestURL, apiClient, onLoad, computeData]);

  let content;
  if (loading || externalLoading) {
    content = (
      <CircularLoading
        centered={centeredLoading}
        style={heightStyle}
        message={name && (Array.isArray(name)
          ? name.map(n => `Loading ${n}`)
          : ['Loading', name])}
      />
    );
  } else if (error || externalError) {
    content = (
      <ErrorComponent
        error={error || externalError}
        title={`Error accessing ${name}`}
        style={heightStyle}
      />
    );
  } else {
    content = (
      <div className="grb-graph-tile-comp-wrapper" style={heightStyle}>
        {children}
        {Component && (<Component {...rest} data={data} />)}
      </div>
    );
  }

  // Wrapper is used so that sizing props applied on the element is not mixed with
  // any margins
  return (
    <div
      className={classnames('grb-graph-tile-wrapper', display && `grb-graph-tile-wrapper-${display}`)}
      style={style}
    >
      <Paper
        className={classnames('grb-graph-tile', className, noHeader && 'grb-graph-tile-no-header')}
        role="tabpanel"
        id={`${id}-graph-tile`}
        elevation={elevation || 0}
      >
        <header>
          {HeaderIcon && <HeaderIcon className="grb-graph-tile-icon" />}
          <Typography>{title}</Typography>
          <div style={FLEX_STYLE} />
          {toolbar && (
            <React.Fragment>
              <div className="grb-graph-tile-toolbar">
                {toolbar}
              </div>
            </React.Fragment>
          )}
          {buttons.filter(({ isValid }) => isValid()).map(({ Button }, i) => (
            <Button key={makeButtonKey(i)} id={id} display={display} />
          ))}
        </header>
        <main>
          {content}
        </main>
      </Paper>
    </div>
  );
}

GraphTile.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  title: PropTypes.string.isRequired,
  icon: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({}),
    PropTypes.string,
  ]),
  Icon: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({}),
  ]),
  className: PropTypes.string,
  Component: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({}),
  ]),
  viewService: PropTypes.shape({
    getIcon: PropTypes.func,
  }),
  toolbar: PropTypes.node,
  children: PropTypes.node,
  style: PropTypes.shape({}),
  display: PropTypes.string,
  onChangeDisplay: PropTypes.func,
  disableFullDisplay: PropTypes.bool,
  noHeader: PropTypes.bool,
  elevation: PropTypes.number,
  actions: PropTypes.shape({}),
  barActions: PropTypes.arrayOf(PropTypes.string),
  onLoad: PropTypes.func,
  apiClient: PropTypes.func,
  requestURL: PropTypes.string,
  updateTime: PropTypes.number,
  computeData: PropTypes.func,
  centeredLoading: PropTypes.bool,
  loading: PropTypes.bool,
  error: PropTypes.shape({}),
};

GraphTile.defaultProps = {
  id: undefined,
  name: undefined,
  icon: undefined,
  Icon: undefined,
  className: undefined,
  toolbar: undefined,
  children: undefined,
  Component: undefined,
  style: undefined,
  display: undefined,
  viewService: undefined,
  onChangeDisplay: undefined,
  disableFullDisplay: false,
  noHeader: false,
  elevation: undefined,
  apiClient: undefined,
  actions: {},
  barActions: [],
  onLoad: undefined,
  requestURL: undefined,
  updateTime: 0,
  computeData: undefined,
  centeredLoading: undefined,
  loading: undefined,
  error: undefined,
};

export {
  GraphTile as default,
  FULL_DISPLAY,
  FULL_ROW_DISPLAY,
  HIDDEN_DISPLAY,
  NORMAL_DISPLAY,
};
